import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createTheme, ThemeProvider, makeStyles } from '@material-ui/core/styles';
import MUIDataTable from 'mui-datatables';
import {
  Icon,
  IconButton,
  TextField,
  MenuItem,
  Dialog,
  DialogContent,
  DialogTitle
} from '@material-ui/core';
import { ConfirmDialog } from 'components';
import {
  saveBannerSlideApi,
  deleteBannerSlideApi
} from 'api';
import EditBannerSlide from './editBannerSlide';


const getMuiTheme = () => createTheme({
  overrides: {
    MUIDataTable: {
      paper: {
        borderRadius: '8px',
        border: '1px solid #ccc',
        overflow: 'hidden',
        padding: '0 15px'
      }
    },
    MuiTableCell: {
      head: {
        fontWeight: '600'
      },
    },
    MuiButton: {
      root: {
        fontWeight: '600'
      }
    }
  }
});

const useStyles = makeStyles({
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  canceleButton: {
    '&:hover': {
      cursor: 'pointer',
      color: 'red',
    }
  }
});

const BannerSlidesList = ({data}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [confirmChangeSlideStatus, setConfirmChangeSlideStatus] = useState(false);
  const [confirmDeleteSlide, setConfirmDeleteSlide] = useState(false);
  const [currentSlideId, setCurrentSlideId] = useState(null);
  const [updatedSlideStatus, setUpdatedSlideStatus] = useState(null);
  const [openEditSlideDialog, setOpenEditSlideDialog] = useState(false);

  const {profile: {stores = []}} = useSelector((state) => state.user);
  const baseUrl = stores.length && stores[0].apiUrl;

  const editableSlideData = currentSlideId && data.find((slide) => slide.slide_id === currentSlideId);

  const columns = [
    {
      name: 'banner_image',
      label: 'Banner Image',
      options: {
        sort: false,
        setCellHeaderProps: () => ({className: 'max-w-300', style: {width: '25%'}}),
        customBodyRenderLite: (dataIndex) => {
          const {image, alt_text, hosted_image_url, hosted_image} = data[dataIndex];
          const imageSrc = hosted_image == 1 ? hosted_image_url : `${baseUrl}/media/flexslider/${image}`;

          return (
            <>
              <img src={imageSrc} alt={alt_text} className="max-w-full" />
            </>
          );
        }
      }
    },
    {
      name: 'alt_text',
      label: 'Banner Title',
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const altText = data[dataIndex].alt_text;

          return <div className="whitespace-pre-wrap">{altText}</div>;
        }
      }
    },
    {
      name: 'slide_startdate',
      label: 'Start Date',
      options: {
        sort: false,
        setCellHeaderProps: () => ({className: 'min-w-100'}),
        customBodyRenderLite: (dataIndex) => {
          const {slide_startdate} = data[dataIndex];
          const startDate = slide_startdate ? new Date(slide_startdate.replace(/-/g, '/')) : null;

          return (
            <>
              {startDate ? startDate.toLocaleDateString() : slide_startdate}
            </>
          );
        }
      }
    },
    {
      name: 'slide_enddate',
      label: 'End Date',
      options: {
        sort: false,
        setCellHeaderProps: () => ({className: 'min-w-100'}),
        customBodyRenderLite: (dataIndex) => {
          const {slide_enddate} = data[dataIndex];
          const endDate = slide_enddate ? new Date(slide_enddate.replace(/-/g, '/')) : null;

          return (
            <>
              {endDate ? endDate.toLocaleDateString() : slide_enddate}
            </>
          );
        }
      }
    },
    {
      name: 'is_active',
      label: 'Status',
      options: {
        sort: false,
        setCellHeaderProps: () => ({style: {width: '10%'}}),
        customBodyRenderLite: (dataIndex) => {
          const {is_active, slide_id} = data[dataIndex];

          return (
            <>
              <TextField
                select
                fullWidth
                name="is_active"
                variant="outlined"
                value={is_active}
                className="text-center"
                onChange={(event) => {
                  setUpdatedSlideStatus(event.target.value);
                  setConfirmChangeSlideStatus(true);
                  setCurrentSlideId(slide_id);
                }}
                size="small"
              >
                <MenuItem value="1"><span className="text-green">Published</span></MenuItem>
                <MenuItem value="0"><span className="text-error">Draft</span></MenuItem>
              </TextField>
            </>
          );
        }
      }
    },
    {
      name: 'slide_sort_order',
      label: 'Sort Order',
      options: {
        setCellHeaderProps: () => ({className: 'text-center no-word-break', style: {width: '10%'}}),
        setCellProps: () => ({className: 'pl-5'}),
        sort: true,
      }
    },
    {
      name: 'actions',
      label: 'Actions',
      options: {
        sort: false,
        setCellHeaderProps: () => ({className: 'text-right pr-8', style: {width: '13%'}}),
        customBodyRenderLite: (dataIndex) => {
          const {slide_id} = data[dataIndex];

          return (
            <div className="text-right">
              <IconButton
                title="Delete"
                onClick={() => {
                  setConfirmDeleteSlide(true);
                  setCurrentSlideId(slide_id);
                }}
              >
                <Icon>delete</Icon>
              </IconButton>
              <IconButton
                title="Edit"
                className="ml-2"
                onClick={() => {
                  setCurrentSlideId(slide_id);
                  setOpenEditSlideDialog(true);
                }}
              >
                <Icon>edit</Icon>
              </IconButton>
            </div>
          );
        }
      }
    }
  ];

  const handleDeleteBannerSlide = () => {
    deleteBannerSlideApi({dispatch, id: currentSlideId});
  };

  const updateBannerSlideStatus = async () => {
    const {
      slide_id,
      title,
      image,
      slide_startdate,
      slide_enddate,
      slide_sort_order,
      alt_text,
      url,
      url_target,
      group_id,
      hosted_image_url,
      hosted_image_thumburl,
      hosted_image
    } = editableSlideData;
    const slideData = {
      slide_id,
      alt_text,
      url,
      url_target,
      title,
      slide_startdate,
      slide_enddate,
      slide_sort_order,
      image,
      hosted_image: 1,
      group_id,
      hosted_image_url: hosted_image == 0
        ? `${baseUrl}/media/flexslider/${image}`
        : hosted_image_url,
      hosted_image_thumburl: hosted_image == 0
        ? `${baseUrl}/media/flexslider/${image}`
        : hosted_image_thumburl,
      is_active: updatedSlideStatus
    };

    saveBannerSlideApi({dispatch, slideData});
  };

  return (
    <ThemeProvider theme={getMuiTheme}>
      <MUIDataTable
        data={data}
        columns={columns}
        options={{
          responsive: 'standard',
          selectableRows: 'none',
          search: false,
          filter: false,
          download: false,
          print: false,
          pagination: false,
          viewColumns: false,
          elevation: 0,
          rowsPerPage: 20,
          rowsPerPageOptions: [20, 40, 80, 100]
        }}
      />
      <ConfirmDialog
        title="Change Slide Status?"
        open={confirmChangeSlideStatus}
        setOpen={setConfirmChangeSlideStatus}
        onConfirm={updateBannerSlideStatus}
      >
        Current slide is {' '}
        <strong>{updatedSlideStatus == '0' ? 'Published' : 'Draft'}</strong>.{' '}
        Are you sure you want to change this slide status?
      </ConfirmDialog>
      <ConfirmDialog
        title="Delete Slide?"
        open={confirmDeleteSlide}
        setOpen={setConfirmDeleteSlide}
        onConfirm={handleDeleteBannerSlide}
      >
        Are you sure you want to delete this slide?
      </ConfirmDialog>
      {editableSlideData &&
        <Dialog
          open={openEditSlideDialog}
          onClose={() => setOpenEditSlideDialog(false)}
          aria-label="edit-banner-slide"
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            <div className={classes.titleContainer}>
              <div>
                Edit {editableSlideData.alt_text}
              </div>
              <Icon
                onClick={() => setOpenEditSlideDialog(false)}
                className={classes.canceleButton}
              >
                cancel
              </Icon>
            </div>
          </DialogTitle>
          <DialogContent>
            <EditBannerSlide data={editableSlideData} baseUrl={baseUrl} isOpen={setOpenEditSlideDialog} />
          </DialogContent>
        </Dialog>
      }
    </ThemeProvider>
  );
};

export default BannerSlidesList;
